import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { TaxSummary, TaxSummaryRow } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import { Empty, ErrorBanner, Loading, Money, Panel, useDateRange } from '../../components/ui'

/**
 * VAT and withholding tax over a period — what was charged, what was paid over, what is still owed
 * (BUILD_PLAN §6 step 6).
 *
 * <p>The endpoint existed with no page, so these figures were only reachable inside the tax advisor
 * pack. That is the wrong way round: the pack is the monthly handoff, and this is the report the
 * person filling in the return reads first.</p>
 *
 * <p><b>Closing is the number that matters.</b> Opening plus charged less settled, stated as a
 * positive amount owed to the authority whichever side the account naturally sits on — nobody
 * filling in a VAT return should have to reason about a credit balance.</p>
 */
export function TaxSummaryPage() {
  const { can } = useAuth()
  const { from, to, setFrom, setTo } = useDateRange()

  const report = useQuery<TaxSummary>(
    `/reports/tax-summary${query({ from, to })}`,
    [from, to],
  )

  const sections = report.data
    ? [
        { title: 'VAT', rows: report.data.vat },
        { title: 'Withholding tax', rows: report.data.withholding },
        { title: 'Other tax accounts', rows: report.data.other },
      ].filter((section) => section.rows.length > 0)
    : []

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>

        <div className="spacer" />

        {can(P.reports.export) && (
          <button
            onClick={() =>
              void download(`/reports/tax-summary/excel${query({ from, to })}`, 'tax-summary.xlsx')
            }
          >
            Export to Excel
          </button>
        )}

        {/* The full monthly handoff — this report plus the trial balance, P&L and G&A in one
            workbook. Separately gated, because it is the whole picture rather than one report. */}
        {can(P.reports.taxPack) && (
          <button
            onClick={() =>
              void download(
                `/reports/tax-advisor-pack${query({ from, to })}`,
                'tax-advisor-pack.xlsx',
              )
            }
          >
            Tax advisor pack
          </button>
        )}
      </div>

      <ErrorBanner error={report.error} />
      {report.loading && <Loading />}

      {report.data && sections.length === 0 && (
        <Panel>
          <Empty>
            Nothing posted to a tax account in this period. The accounts are seeded under the
            tax-authority group; VAT and withholding appear here as soon as an entry touches them.
          </Empty>
        </Panel>
      )}

      {sections.map((section) => (
        <Section key={section.title} title={section.title} rows={section.rows} />
      ))}
    </>
  )
}

function Section({ title, rows }: { title: string; rows: TaxSummaryRow[] }) {
  const total = (pick: (row: TaxSummaryRow) => number) => rows.reduce((sum, r) => sum + pick(r), 0)

  return (
    <Panel title={title} flush>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th style={{ width: 110 }}>Code</th>
              <th>Account</th>
              <th style={{ width: 130 }} className="num">
                Opening
              </th>
              <th style={{ width: 130 }} className="num">
                Charged
              </th>
              <th style={{ width: 130 }} className="num">
                Settled
              </th>
              <th style={{ width: 140 }} className="num">
                Closing
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.code}>
                <td className="code">{row.code}</td>
                <td>{row.name}</td>
                <td className="num">
                  <Money value={row.opening} blankZero />
                </td>
                <td className="num">
                  <Money value={row.charged} blankZero />
                </td>
                <td className="num">
                  <Money value={row.settled} blankZero />
                </td>
                <td className="num strong">
                  <Money value={row.closing} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>Total</td>
              <td className="num">
                <Money value={total((r) => r.opening)} />
              </td>
              <td className="num">
                <Money value={total((r) => r.charged)} />
              </td>
              <td className="num">
                <Money value={total((r) => r.settled)} />
              </td>
              <td className="num strong">
                <Money value={total((r) => r.closing)} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Panel>
  )
}
